import React, { useEffect, useMemo, useState } from 'react'
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Select, TextField, Typography, makeStyles
} from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import api from '../api/client'
import { toFarsiDigits } from '../assets/utils'

const useStyles = makeStyles((theme) => ({
  dialog: { direction: 'rtl' },
  title: {
    background: 'linear-gradient(135deg, #1a237e 0%, #283593 100%)',
    color: '#fff', padding: theme.spacing(2, 3),
  },
  content: { padding: theme.spacing(2) },
  section: { marginBottom: theme.spacing(2) },
  sectionTitle: { fontWeight: 800, fontSize: 13.5, color: '#1a237e', marginBottom: 6 },
  hint: { fontSize: 12, color: '#5a6b8c', lineHeight: 1.9, marginBottom: 8 },
  typeRow: { display: 'flex', gap: 6, flexWrap: 'wrap' },
  typeChip: {
    cursor: 'pointer',
    fontWeight: 700,
    fontSize: 12,
    '&.on': { background: '#8fa8f8', color: '#fff' },
  },
  listBox: {
    maxHeight: 200, overflowY: 'auto',
    border: '1px solid #e3e6f2', borderRadius: 10,
  },
  item: { borderBottom: '1px solid #f0f2f8', '&:last-child': { borderBottom: 'none' } },
  contractChip: { fontSize: 10.5, marginInlineStart: 4 },
}))

const ROLE_COLORS = {
  Admin: { bg: '#e8eafd', fg: '#1a237e' },
  Department: { bg: '#e3f2fd', fg: '#1565c0' },
  DepartmentManager: { bg: '#e0f2f1', fg: '#00796b' },
  Contractor: { bg: '#f3e5f5', fg: '#7b1fa2' },
  Consultant: { bg: '#fff3e0', fg: '#ef6c00' },
  Supervisor: { bg: '#e0f7fa', fg: '#00838f' },
}

// انواعِ مجازِ مخاطب به‌تفکیکِ نقشِ فرستنده (قاعدهٔ کارفرما)
const TYPES_BY_ROLE = {
  Contractor: ['Admin', 'Department', 'DepartmentManager', 'Supervisor', 'Consultant'],
  Supervisor: ['Admin', 'Department', 'DepartmentManager', 'Contractor', 'Consultant'],
  Consultant: ['Admin', 'Department', 'DepartmentManager', 'Contractor', 'Supervisor'],
  Department: ['Admin', 'Department', 'DepartmentManager', 'Contractor', 'Supervisor', 'Consultant'],
  DepartmentManager: ['Admin', 'Department', 'DepartmentManager', 'Contractor', 'Supervisor', 'Consultant'],
  Admin: ['Admin', 'Department', 'DepartmentManager', 'Contractor', 'Supervisor', 'Consultant'],
}

const TYPE_LABEL = {
  Admin: 'ادمین',
  Department: 'کاربر سازمان',
  DepartmentManager: 'مدیر سازمان',
  Contractor: 'پیمانکار',
  Supervisor: 'ناظر',
  Consultant: 'مشاور',
}

// نوع‌هایی که «سازمان» برایشان لازم است
const NEEDS_ORG = ['Department', 'DepartmentManager', 'Contractor', 'Supervisor', 'Consultant']

/**
 * دیالوگِ «گفتگوی جدید»:
 *   ۱) نوعِ مخاطب انتخاب می‌شود
 *   ۲) اگر لازم باشد، سازمان انتخاب می‌شود
 *   ۳) مخاطب با AutoComplete از میانِ کاربرانِ «همان نوع و همان سازمان» انتخاب می‌شود
 */
export default function NewConversationDialog({ open, onClose, onCreated, currentRole }) {
  const classes = useStyles()
  const [boot, setBoot] = useState({ direct: [], peers: [], everyone: [], organizations: [], can_message_anyone: false })
  const [loading, setLoading] = useState(false)
  const [subject, setSubject] = useState('')
  const [type, setType] = useState('')
  const [deptId, setDeptId] = useState('')
  const [options, setOptions] = useState([])
  const [optLoading, setOptLoading] = useState(false)
  const [picked, setPicked] = useState(null)
  const [contractId, setContractId] = useState('')
  const [peerContracts, setPeerContracts] = useState([])
  const [err, setErr] = useState('')

  useEffect(() => {
    if (!open) return
    setLoading(true)
    setErr('')
    api.get('messages/targets/').then((res) => {
      if (res.status === 200) {
        setBoot({
          direct: res.data?.direct || [],
          peers: res.data?.peers || [],
          everyone: res.data?.everyone || [],
          organizations: res.data?.organizations || [],
          can_message_anyone: !!res.data?.can_message_anyone,
        })
      }
    }).catch(() => setErr('خطا در دریافت فهرست گیرنده‌ها'))
      .finally(() => setLoading(false))
  }, [open])

  // بارگذاریِ گزینه‌ها بر اساسِ نوع + سازمان
  useEffect(() => {
    if (!open || !type) { setOptions([]); return }
    setOptLoading(true)
    const params = { type }
    if (deptId) params.department_id = deptId
    api.get('messages/targets/', { params }).then((res) => {
      if (res.status === 200) setOptions(res.data?.results || [])
    }).catch(() => setOptions([]))
      .finally(() => setOptLoading(false))
  }, [open, type, deptId])

  // وقتی «نوع» یا «سازمان» عوض شد ⇒ انتخابِ قبلی بی‌اعتبار
  useEffect(() => {
    setPicked(null)
    setContractId('')
    setPeerContracts([])
  }, [type, deptId])

  // اگر «ادمین» انتخاب شد و فقط یک ادمین هست ⇒ خودکار انتخاب شود
  useEffect(() => {
    if (type === 'Admin' && options.length === 1 && !picked) {
      setPicked(options[0])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, options])

  const allowedTypes = useMemo(() => {
    const role = currentRole || 'Contractor'
    return TYPES_BY_ROLE[role] || TYPES_BY_ROLE.Contractor
  }, [currentRole])

  const orgsForType = useMemo(() => {
    if (!NEEDS_ORG.includes(type)) return []
    // «سازمان» ⇒ شناسهٔ سازمان (department_id) — نه شناسهٔ کاربر
    return (boot.organizations || []).map((o) => ({
      id: o.id,
      name: o.name || o.short_name || `#${o.id}`,
      role: 'Department',
    }))
  }, [type, boot])

  // قراردادهای مشترک با مخاطبِ انتخاب‌شده (برای نقش‌های قراردادی)
  useEffect(() => {
    if (!picked || !picked.id) { setPeerContracts([]); return }
    const peer = (boot.peers || []).find((p) => p.id === picked.id)
    setPeerContracts(peer?.contracts || [])
  }, [picked, boot.peers])

  const reset = () => {
    setSubject(''); setType(''); setDeptId(''); setOptions([])
    setPicked(null); setContractId(''); setPeerContracts([]); setErr('')
  }

  const handleClose = () => { reset(); onClose?.() }

  const handleCreate = async () => {
    setErr('')
    if (!type) { setErr('نوعِ مخاطب را انتخاب کنید.'); return }
    if (!picked || !picked.id) { setErr('مخاطب را انتخاب کنید.'); return }
    if (!subject.trim()) { setErr('موضوعِ پیام را بنویسید.'); return }
    const needsContract = NEEDS_ORG.includes(type) && type !== 'Department' && type !== 'DepartmentManager'
    if (needsContract && !contractId) {
      setErr('برای تبادل با پیمانکار/ناظر/مشاور، انتخابِ قرارداد الزامی است.')
      return
    }
    try {
      const res = await api.post('messages/', {
        contract_id: contractId || '',
        receiver_id: picked.id,
        subject: subject.trim(),
        message: 'سلام',
      })
      if (res.status === 201) {
        const info = {
          contractId: contractId ? Number(contractId) : null,
          partnerId: picked.id,
          partnerName: picked.name,
          subject: subject.trim(),
        }
        handleClose()
        onCreated?.(info)
      }
    } catch (e) {
      setErr(e?.response?.data?.error || 'خطا در ایجاد گفتگو')
    }
  }

  // نامِ انتخاب‌شده به‌همراه برچسبِ نقش (برای نمایش در کادرِ مخاطب)
  const pickedLabel = picked
    ? `${picked.name || ''}${picked.role_label ? ' — ' + picked.role_label : ''}`
    : ''

  return (
    <Dialog open={!!open} onClose={handleClose} fullWidth maxWidth="sm" className={classes.dialog}>
      <DialogTitle className={classes.title}>
        <Typography variant="h6" style={{ fontWeight: 'bold' }}>گفتگوی جدید</Typography>
      </DialogTitle>

      <DialogContent className={classes.content}>
        {loading && <Typography color="textSecondary">در حال بارگذاری…</Typography>}
        {err && <Typography color="error" style={{ marginBottom: 8 }}>{err}</Typography>}

        {!loading && (
          <>
            {/* ── ۱) نوعِ مخاطب ── */}
            <Box className={classes.section}>
              {allowedTypes.includes('Admin') && (
                <Box style={{ marginBottom: 10 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={() => setType('Admin')}
                    style={{ fontWeight: 800, textTransform: 'none' }}
                  >
                    ✉️ ارسال به مدیر سامانه (بدون قرارداد)
                  </Button>
                  <Typography className={classes.hint} style={{ marginTop: 4 }}>
                    برای پشتیبانی و سؤال از مدیر سامانه؛ نیازی به انتخاب قرارداد نیست.
                  </Typography>
                </Box>
              )}
              <Typography className={classes.sectionTitle}>نوعِ مخاطب</Typography>
              <Box className={classes.typeRow}>
                {allowedTypes.map((t) => (
                  <Chip
                    key={t}
                    label={TYPE_LABEL[t] || t}
                    onClick={() => setType(t)}
                    className={`${classes.typeChip} ${type === t ? 'on' : ''}`}
                    style={type === t ? { background: '#8fa8f8', color: '#fff' } : { background: '#e0e0e0', color: '#37474f' }}
                  />
                ))}
              </Box>
            </Box>

            {/* ── ۲) سازمان (برای نوع‌هایی که لازم است) ── */}
            {type && NEEDS_ORG.includes(type) && (
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>سازمان</Typography>
                <Select
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={deptId}
                  onChange={(e) => setDeptId(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>— انتخابِ سازمان —</MenuItem>
                  {orgsForType.map((o) => (
                    <MenuItem key={o.id} value={o.id}>
                      {toFarsiDigits(o.name)}
                    </MenuItem>
                  ))}
                </Select>
                {NEEDS_ORG.includes(type) && type !== 'Department' && type !== 'DepartmentManager' && (
                  <Typography className={classes.hint} style={{ marginTop: 6 }}>
                    برای تبادلِ پیام با پیمانکار/ناظر/مشاور، <b>تعیینِ قرارداد الزامی است</b>.
                  </Typography>
                )}
                {(type === 'Department' || type === 'DepartmentManager') && (
                  <Typography className={classes.hint} style={{ marginTop: 6 }}>
                    پیام به سازمان یا مدیرِ سازمان <b>نیازی به تعیینِ قرارداد ندارد</b>.
                  </Typography>
                )}
              </Box>
            )}

            {/* ── ۳) انتخابِ مخاطب (AutoComplete) ── */}
            {type && (
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>مخاطب</Typography>
                <Autocomplete
                  options={options}
                  loading={optLoading}
                  value={picked}
                  onChange={(e, v) => setPicked(v)}
                  getOptionLabel={(o) => `${o.name || ''} — ${o.role_label || o.role || ''}`}
                  isOptionEqualToValue={(a, b) => a && b && a.id === b.id}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      variant="outlined"
                      label="جستجو و انتخابِ مخاطب"
                      placeholder={optLoading ? 'در حال بارگذاری…' : 'نام یا نامِ کاربری'}
                      InputProps={{ ...params.InputProps, endAdornment: (
                        <>
                          {optLoading ? <Typography color="textSecondary" variant="caption">…</Typography> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ) }}
                    />
                  )}
                />
                {!optLoading && !options.length && (
                  <Typography className={classes.hint} style={{ marginTop: 6 }}>
                    کاربری با این نوع در این سازمان یافت نشد.
                  </Typography>
                )}
              </Box>
            )}

            {/* ── ۴) قرارداد (برای نقش‌های قراردادی) ── */}
            {picked && peerContracts.length > 0 && (
              <Box className={classes.section}>
                <Typography className={classes.sectionTitle}>انتخابِ قرارداد</Typography>
                <Select
                  fullWidth
                  size="small"
                  variant="outlined"
                  value={contractId}
                  onChange={(e) => setContractId(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="" disabled>— انتخابِ قرارداد —</MenuItem>
                  {peerContracts.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {toFarsiDigits(c.number)} {c.organization ? `· ${toFarsiDigits(c.organization)}` : ''}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
            )}

            {/* ── ۵) موضوع ── */}
            <Box className={classes.section}>
              <TextField
                fullWidth
                size="small"
                variant="outlined"
                label="موضوعِ گفتگو *"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions style={{ padding: 16 }}>
        <Button onClick={handleClose} color="primary">انصراف</Button>
        <Button onClick={handleCreate} variant="contained" color="primary" style={{ fontWeight: 700 }}>
          شروعِ گفتگو
        </Button>
      </DialogActions>
    </Dialog>
  )
}
